import { css } from '@emotion/react';
import styled from '@emotion/styled';
import * as successCircleIcon from '~/assets/icons/icon_success.svg';
import { DangerButton, GreyPrimaryAlternativeButton, PrimaryButton } from '~/components/Button';
import Modal from '~/components/Modal';
import { ResponsiveBreakPoints } from '~/constants/responsive';
import * as React from 'react';
import { colors, fontWeight } from '~/styles/constants';

type DialogType = 'success' | 'error';

export interface DialogProps {
  isOpen: boolean;
  isLoading?: boolean;
  onConfirm?: () => void;
  confirmButtonText?: string;
  onClose: () => void;
  closeButtonText: string;
  message: string | React.Component | Element | JSX.Element | string[];
  title?: string | React.Component | Element | JSX.Element;
}

interface StyledDialogProps {
  dialogType?: DialogType;
}

const Body = styled('div')<StyledDialogProps>`
  background-color: ${(props) =>
    props.dialogType === 'success'
      ? colors.dialog.successBackground
      : props.dialogType === 'error'
        ? colors.dialog.errorBackground
        : colors.dialog.infoBackground};
  flex-grow: 1;
  display: flex;
  padding: 30px 30px;
  align-items: center;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
`;

// Success Icon
const SuccessIconImage = styled('img')`
  box-shadow: 0 2px 4px 0 ${colors.forest50};
  border-radius: 20px;
`;
const SuccessIcon = () => <SuccessIconImage src={successCircleIcon} />;
const baseIcon = css`
  vertical-align: middle;
  font-size: 2em;
`;
const StyledSuccessIcon = styled(SuccessIcon)`
  ${baseIcon}
  color: ${colors.primary};
`;

// Error Icon
interface AlertIconProps {
  className?: string;
}
const AlertIcon = (props: AlertIconProps) => (
  <i className={`fa fa-exclamation-triangle ${props.className}`} aria-hidden="true" />
);
const StyledAlertIcon = styled(AlertIcon)`
  ${baseIcon}
  color: ${colors.dangerRed};
`;

const Content = styled('div')`
  margin-left: 15px;
  font-size: 14px;
  font-weight: normal;
  line-height: 20px;
  color: ${colors.fontColor};
`;

const Title = styled('div')`
  font-weight: ${fontWeight.medium};
  margin-bottom: 3px;
  padding-top: 30px;
`;

const FullWidthContainer = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    width: initial;
    display: inline-flex;
  }
`;

const Dialog = (props: DialogProps & StyledDialogProps) => {
  const title = props.title ? <Title>{props.title}</Title> : null;
  const renderIcon = () => {
    const { dialogType } = props;
    if (dialogType === 'success') {
      return <StyledSuccessIcon />;
    }
    if (dialogType === 'error') {
      return <StyledAlertIcon />;
    }
    return null;
  };

  const renderActionButton = () => {
    const { dialogType, onConfirm, confirmButtonText, isLoading } = props;
    if (onConfirm) {
      if (dialogType === 'success') {
        return (
          <PrimaryButton isLoading={isLoading} disabled={isLoading} onClick={onConfirm} fullWidth>
            {confirmButtonText}
          </PrimaryButton>
        );
      }
      if (dialogType === 'error') {
        return (
          <DangerButton isLoading={isLoading} disabled={isLoading} onClick={onConfirm} fullWidth>
            {confirmButtonText}
          </DangerButton>
        );
      }
    }
    return null;
  };

  const renderMessage = () => {
    const { message } = props;
    if (Array.isArray(message)) {
      return (
        <div>
          {message.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        </div>
      );
    }
    return message;
  };

  return (
    <Modal.Container isOpen={props.isOpen} size="sm" onClose={props.onClose} isDialog>
      <Body dialogType={props.dialogType}>
        {renderIcon()}
        <Content>
          {title}
          {renderMessage()}
        </Content>
      </Body>
      <Modal.Footer>
        <FullWidthContainer>
          <GreyPrimaryAlternativeButton onClick={props.onClose} fullWidth>
            {props.closeButtonText}
          </GreyPrimaryAlternativeButton>
          {renderActionButton()}
        </FullWidthContainer>
      </Modal.Footer>
    </Modal.Container>
  );
};

export default Dialog;
